import {clearFeedbacksByName, clearInputsByName, fillInputsByName, getInputsByName} from "../../helpers/forms";
import {initFlatPickerInput} from "../../helpers/inputs/flatPicker/flatPicker";
import {loading, stopLoading} from "../../helpers/loading";
import Axios from "axios";
import handleAjaxError from "../../helpers/handleAjaxError";
import {SingleFileUploadComponent} from "../../helpers/inputs/singleFileUpload/SingleFileUploadComponent";
import {initTomSelectInput} from "../../helpers/inputs/tom-select/tom-select";
import {
	displayLocalValidationError,
	interceptValidationErrors,
	noValidationErrorsLeft
} from "../../helpers/forms/validationErrors/utils";

/**
 * @param {HTMLElement} formElement
 */
function initFreightForm(formElement) {
	// Setup buttons & checkboxes
	const addDispatcherButton = formElement.querySelector('.add-dispatcher-button');
	addDispatcherButton.addEventListener('click', addDispatcher);

	/** @type {HTMLInputElement} */
	const mailingAddressCheckbox = formElement.querySelector('input[name="has_mailing_address"]');

	mailingAddressCheckbox.addEventListener('change', handleMailingAddressToggle);

	/** @type {HTMLInputElement} */
	formElement.querySelectorAll('input[name="has_factoring_company"]').forEach(input => {
		input.addEventListener('change', handleFactoringCompanyToggle)
	});

	// Setup date fields
	const dateField = formElement.querySelector('input[name="date"]');
	const dateFieldInstance = initFlatPickerInput(dateField, {
		enableTime: false,
	});

	// Setup dispatcher template & container
	const dispatcherContainerTemplate =
		formElement.querySelector('.dispatcher-container')

	const dispatchersContainers = formElement.querySelector('.dispatchers-container');

	// Setup DOT search
	const dotInput = formElement.querySelector('input[name="us_dot"]');

	dotInput.addEventListener('keyup', event => {
		if (event.key === 'Enter') {
			searchDotNumber();
		}
	})

	const dotInputButton = formElement.querySelector('.search-us-dot-button');
	dotInputButton.addEventListener('click', searchDotNumber);

	// Init dispatchers
	formElement.querySelectorAll('.dispatchers-container .dispatcher-container').forEach(dispatcher => {
		dispatcher.querySelector('.remove-dispatcher-button')
			.addEventListener('click', () => {
				dispatcher.remove();
			})
	})

	// Init media containers, if they exists
	const authorityUploadContainer = document.querySelector('.authority-upload-container');
	const w9UploadContainer = document.querySelector('.w_9-upload-container');

	/** @type {SingleFileUploadComponent|undefined} */
	let authorityFileUpload;
	if (authorityUploadContainer) {
		authorityFileUpload = new SingleFileUploadComponent(
			authorityUploadContainer
		);
	}

	/** @type {SingleFileUploadComponent|undefined} */
	let w9Upload;
	if (w9UploadContainer) {
		w9Upload = new SingleFileUploadComponent(
			w9UploadContainer
		);
	}

	// Init factoring company select

	const factoringCompanyContainer = formElement.querySelector('.factoring-company-container');

	const factoringCompanySelect = formElement.querySelector('select[name="factoring_company_id"]');

	const factoringCompanySelectInstance = initTomSelectInput(
		factoringCompanySelect,
		{
			onDropdownOpen() {
				// Check for upload container to check if we are in admin view
				if (!w9UploadContainer) {
					// Force container to scroll all the way to display dropdown properly
					const scrollingParent = formElement.closest('.card-body');
					if (scrollingParent) {
						scrollingParent.scrollTop = formElement.scrollHeight;
					}
				}
			},
			// Need to increase z index to beat file uploaders'
			onChange: () => {
				handleFactoringCompanySelect()
			},
			allowEmptyOption: true,
		}
	);

	// Init phone input
	const phoneInputs = [
		'company_phone',
		'fax',
		'after_hours_cell',
	].map(inputName => formElement.querySelector(`input[name=${inputName}]`));

	phoneInputs.forEach(input => {
		input.addEventListener('keyup', () => handlePhoneInput(input));
		input.addEventListener('change', () => handlePhoneInput(input));
	})

	// Initial render
	handleMailingAddressToggle();
	handleFactoringCompanyToggle();
	handleFactoringCompanySelect();

	return {
		formElement,
		formFields: [
			'id',

			'date',
			'company_name',
			'dba_name',

			'us_dot',

			// Physical address

			'physical_address',
			'city',
			'state',
			'zip',

			// Mailing

			'mailing_address',
			'mailing_city',
			'mailing_state',
			'mailing_zip',

			// Company Info

			'company_phone',
			'fax',
			'after_hours_cell',
			'email',
			'ssn',
			'ein',
			'mc',
			'units',
			'company_owned',
			'owner_operators',
			'is_smartway_carrier',

			// Factoring company

			'factoring_company_id',
			'factoring_company',
			'factoring_company_address',
			'factoring_company_address_2',
		],
		validate() {
			// Client side validation
			// Make sure an option has been manually selected
			// regarding payment address
			const factoringSelectedOption = formElement.querySelector('input[name="has_factoring_company"]:checked');
			if (factoringSelectedOption === null) {
				// Display warning
				displayLocalValidationError(formElement, 'has_factoring_company', 'Select a payment option');

				return false;
			}

			const isSmartwayCarrierOption = formElement.querySelector('input[name="is_smartway_carrier"]:checked');
			if (isSmartwayCarrierOption === null) {
				// Display warning
				displayLocalValidationError(formElement, 'is_smartway_carrier', 'Select an option');

				return false;
			}

			return true;
		},
		inputs() {
			const data = getInputsByName(formElement, this.formFields);

			// Manually retrieve is_smartway_carrier flag
			data.is_smartway_carrier = getIsSmartWayCarrierValue();

			// Retrieve date input manually
			data.date = dateFieldInstance.input.value;

			// Retrieve dispatchers if any
			data.dispatchers = [];

			formElement.querySelectorAll('.dispatchers-container .dispatcher-container')
				.forEach(dispatcherContainer => {
					data.dispatchers.push(
						getInputsByName(
							dispatcherContainer,
							['name', 'phone', 'email']
						)
					);
				})

			// Retrieve media data if set
			if (authorityFileUpload) {
				data.authority_file_id = authorityFileUpload.getFileId();
			}
			if (w9Upload) {
				data.w_9_id = w9Upload.getFileId();
			}

			return data;
		},
		clearFeedbacks() {
			return clearFeedbacksByName(formElement);
		},
		postUrl() {
			return formElement.querySelector('input[name="submit_route"]').value;
		},
		successUrl() {
			return formElement.dataset.successUrl;
		},
		handleValidationErrors(error) {
			// We may need to intercept validation errors and handle them

			// Get all DOM containers for dispatchers entries
			const dispatcherContainers = Array.from(
				dispatchersContainers.querySelectorAll('.form-row')
			);

			const interceptedValidationErrors = interceptValidationErrors(error, [
				{
					pattern: /dispatchers\.\d+\./,
					handler(field, errorString) {
						const propertyIndex = field.split('.').slice(-2, -1)[0];
						const actualField = field.split('.').slice(-1)[0];

						const propertyContainer = dispatcherContainers[propertyIndex];

						// Replace error in string
						errorString = errorString.replace(field, actualField);

						displayLocalValidationError(propertyContainer, actualField, errorString);
					}
				}
			])

			// If we didnt intercept anything or there were errors lefts...
			if (interceptedValidationErrors === false || noValidationErrorsLeft(error) === false) {
				///... default error handler
				handleAjaxError(error, formElement)
			}

			// Keep error going
			throw error;
		}
	};

	// Form methods

	function handleMailingAddressToggle() {
		const mailingAddressValue = mailingAddressCheckbox.checked;

		const mailingAddressContainer = formElement.querySelector('.mailing-address-container');

		if (mailingAddressValue) {
			mailingAddressContainer.classList.remove('d-none');
		} else {
			mailingAddressContainer.classList.add('d-none');
		}
	}

	function handleFactoringCompanySelect() {
		// Clear container
		clearInputsByName(factoringCompanyContainer,
			[
				'factoring_company',
				'factoring_company_address',
				'factoring_company_address_2'
			]
		);

		// Retrieve selected option
		// noinspection JSCheckFunctionSignatures
		const option = factoringCompanySelect.querySelector('option:checked')

		const fieldsContainer = factoringCompanyContainer.querySelector('.factoring-company-fields-container');

		// Check if a company was selected
		if (option.value) {
			try {
				const factoringCompany = JSON.parse(option.dataset.fields);

				// Map fields to match freight form
				// noinspection JSUnresolvedReference
				const mappedFactoringCompany = {
					factoring_company: factoringCompany.name,
					factoring_company_address: factoringCompany.address_1,
					factoring_company_address_2: factoringCompany.address_2,
				}

				// Update fields
				fillInputsByName(fieldsContainer, mappedFactoringCompany);

				// Mark read only
				fieldsContainer.querySelectorAll('input').forEach(input => {
					input.readOnly = true;
				})
			} catch (e) {
				console.log('Error retrieving factoring company data');
				console.error(e);
			}
		} else {
			// Allow changes
			fieldsContainer.querySelectorAll('input').forEach(input => {
				input.readOnly = false;
			})
		}
	}

	function getFactoringCompanyValue() {
		const checkedOption = formElement.querySelector('input[name="has_factoring_company"]:checked');

		if (checkedOption === null) {
			return false
		}

		return checkedOption.value === '1';
	}

	function handleFactoringCompanyToggle() {
		const factoringCompanyValue = getFactoringCompanyValue();

		if (factoringCompanyValue) {
			factoringCompanyContainer.classList.remove('d-none');
		} else {
			factoringCompanyContainer.classList.add('d-none');

			// Clear select and update fields
			factoringCompanySelectInstance.setValue('');
			handleFactoringCompanySelect();
		}
	}

	function getIsSmartWayCarrierValue() {
		const checkedOption = formElement.querySelector('input[name="is_smartway_carrier"]:checked');

		if (checkedOption === null) {
			return false
		}

		return checkedOption.value === '1';
	}


	function addDispatcher() {
		/** @type {HTMLElement} */
		const newDispatcherElement = dispatcherContainerTemplate.cloneNode(true);

		const removeDispatcherButton = newDispatcherElement.querySelector('.remove-dispatcher-button')

		removeDispatcherButton.addEventListener('click', () => {
			newDispatcherElement.remove();
		})

		// Init dispatcher phone input formatter
		const phoneInput = newDispatcherElement.querySelector('input[name="phone"]');

		phoneInput.addEventListener('keyup', () => handlePhoneInput(phoneInput));
		phoneInput.addEventListener('change', () => handlePhoneInput(phoneInput));

		dispatchersContainers.append(newDispatcherElement);
	}

	function searchDotNumber() {
		const route = formElement.querySelector('input[name="pull_carrier_data_router"]')
			.value;

		const dotContainer = formElement.querySelector('.dot-number-container');

		const data = getInputsByName(formElement, ['us_dot']);

		if (!data.us_dot || data.us_dot.length < 1) {
			// Do nothing if there is no search value
			return;
		}

		clearFeedbacksByName(dotContainer);

		loading(dotContainer);

		Axios.post(route, data)
			.then(response => {
				const data = response.data;

				// Delete 'date' entry from carrier data, as it overrides
				// the one in the form
				// noinspection JSUnresolvedReference
				delete data.carrier.date;

				// Fill form
				// noinspection JSUnresolvedReference
				fillInputsByName(formElement, data.carrier);
			})
			.catch(handleAjaxError)
			.finally(() => stopLoading(dotContainer))
	}

	function handlePhoneInput(input) {
		input.value = formatPhone(input.value);
	}

	/**
	 * @param {string} string
	 */
	function formatPhone(string) {
		// Remove all non numbers from string
		string = string.replace(/\D/g, '')
			// Reverse string
			.split('')
			.reverse()
			.join('');

		// Break into parts
		const firstPart = string.substring(0, 4);
		const secondPart = string.substring(4, 7);
		const lastPart = string.substring(7);

		let result = firstPart;
		if (secondPart) {
			result = result + `-${secondPart}`;
		}
		if (lastPart) {
			result = result + `-${lastPart}`;
		}

		return result
			// Reverse string
			.split('')
			.reverse()
			.join('');
	}
}

export {initFreightForm}
